import { z } from 'zod'
import type {
  PressKitConfig,
  PressKitStatDocument,
  PressKitAssetDocument,
  PressKitReleaseDocument,
  PressKitCoverageDocument,
  DerivedStatKey,
} from '@/types'

export type PressKitConfigWithId = PressKitConfig
export type PressKitStatWithId = PressKitStatDocument & { id: string }
export type PressKitAssetWithId = PressKitAssetDocument & { id: string }
export type PressKitReleaseWithId = PressKitReleaseDocument & { id: string }
export type PressKitCoverageWithId = PressKitCoverageDocument & { id: string }

// ─── Derived stat labels ────────────────────────────────────────────────────

export const DERIVED_STAT_LABELS: Record<DerivedStatKey, string> = {
  films_published: 'Films (Published)',
  episodes_published: 'Episodes (Published)',
  seasons_published: 'Seasons (Published)',
  company_members_active: 'Active Company Members',
  artists_worked_with: 'Artists Worked With',
  testimonials_approved: 'Client Testimonials',
  festivals_published: 'Festivals',
}

// ─── Zod schemas ────────────────────────────────────────────────────────────

export const pressKitConfigSchema = z.object({
  bioTitle: z.string().min(1, 'Title is required'),
  bioContent: z.string(),
  contactName: z.string(),
  contactEmail: z.string().email('Invalid email').or(z.literal('')),
  isPublished: z.boolean(),
})

export type PressKitConfigFormValues = z.infer<typeof pressKitConfigSchema>

export const pressKitStatSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('manual'),
    label: z.string().min(1, 'Label is required'),
    value: z.string().min(1, 'Value is required'),
    isVisible: z.boolean(),
  }),
  z.object({
    type: z.literal('derived'),
    label: z.string().min(1, 'Label is required'),
    derivedKey: z.enum([
      'films_published',
      'episodes_published',
      'seasons_published',
      'company_members_active',
      'artists_worked_with',
      'testimonials_approved',
      'festivals_published',
    ] as [DerivedStatKey, ...DerivedStatKey[]]),
    isVisible: z.boolean(),
  }),
])

export type PressKitStatFormValues = z.infer<typeof pressKitStatSchema>

export const pressKitReleaseSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  summary: z.string().min(1, 'Summary is required'),
  content: z.string().min(1, 'Content is required'),
  publishedAt: z.date(),
  isPublished: z.boolean(),
})

export type PressKitReleaseFormValues = z.infer<typeof pressKitReleaseSchema>

export const pressKitCoverageSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  publication: z.string().min(1, 'Publication is required'),
  url: z.string().url('Must be a valid URL'),
  excerpt: z.string(),
  coverageDate: z.date(),
  isVisible: z.boolean(),
})

export type PressKitCoverageFormValues = z.infer<typeof pressKitCoverageSchema>
